import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Sparkles,
  History as HistoryIcon,
  Loader2,
  Plus,
  Send,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { safeFormatDate } from "@/lib/dateUtils";
import { cn } from "@/lib/utils";
import { useBookkeeperChat } from "./BookkeeperChatProvider";
import { useBookkeeperMessages } from "@/hooks/useBookkeeperMessages";
import { useBookkeeperThreads, useDeleteThread, useRenameThread } from "@/hooks/useBookkeeperThreads";
import { BookkeeperMessage } from "./BookkeeperMessage";

/**
 * Which host is rendering the chat.
 *
 * - `"panel"` — the docked, resizable desktop `<aside>` (`BookkeeperPanel`).
 *   Enter sends, Shift+Enter inserts a newline, and there is deliberately no
 *   send button (commit 63972d8c).
 * - `"touch"` — the phone surface at `/org/:orgId/apps/bookkeeper`. A phone
 *   keyboard has no Shift+Enter, so Enter inserts a newline and an explicit
 *   send button submits. Targets and type follow `docs/MOBILE.md` §2/§4.
 */
/**
 * Desktop header icon button — the main app header's ghost `h-9 w-9` button.
 * Exported so the panel's close button (passed in as `headerActions`) matches.
 */
export const BOOKKEEPER_HEADER_BUTTON_CLASS = "h-9 w-9";

export type BookkeeperChatTarget = "panel" | "touch";

export interface BookkeeperChatSurfaceProps {
  /** Host-specific behaviour and sizing. Defaults to the desktop panel. */
  target?: BookkeeperChatTarget;
  /**
   * Rendered at the end of the header's action row, after "new chat". The
   * docked panel passes its close button here; the phone surface passes
   * nothing (its exit lives in the `AppsLayout` header).
   */
  headerActions?: React.ReactNode;
}

/**
 * The Ré chat itself: branded header band → thread dropdown → message scroll
 * region → composer.
 *
 * Returns a **fragment**, never a wrapping element — each host supplies its own
 * bounded flex column (`BookkeeperPanel`'s `<aside>`, `MobileBookkeeper`'s page
 * container). That keeps the desktop panel's rendered DOM byte-identical to
 * what it was before this component was extracted out of it.
 */
export function BookkeeperChatSurface({
  target = "panel",
  headerActions,
}: BookkeeperChatSurfaceProps) {
  const { t } = useTranslation("common");
  const isTouch = target === "touch";
  const chat = useBookkeeperChat();
  const { data: threads = [], isFetched: threadsFetched } = useBookkeeperThreads();
  const { data: messages = [] } = useBookkeeperMessages(chat.activeThreadId);
  const renameThread = useRenameThread();
  const deleteThread = useDeleteThread();

  const [input, setInput] = useState("");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");

  const scrollRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const activeThread = useMemo(
    () => threads.find((th) => th.id === chat.activeThreadId) ?? null,
    [threads, chat.activeThreadId],
  );

  const showEmpty = !chat.activeThreadId && !chat.pending && messages.length === 0;

  // Auto-scroll to the newest message / streamed token / thinking indicator.
  //
  // The empty state is taller than a phone's message area, so "scroll to
  // newest" would clip its icon and heading. Reset to the top rather than
  // merely skipping: the scroll container is the same node in both branches,
  // so arriving at the empty state from a scrolled thread ("+", or deleting
  // the active thread) would otherwise keep the old offset — the browser
  // clamps it to the new maximum instead of zeroing it, which is the same
  // clipped view. The docked panel never sees either case; its empty state
  // fits, so its maximum scrollTop is 0.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (showEmpty) {
      el.scrollTop = 0;
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, [showEmpty, messages, chat.pending, chat.isThinking, chat.streamingText]);

  // Heal a stale restored thread: the persisted id can outlive its row (database
  // reset, retention purge, deletion in another tab), leaving the panel stuck on
  // a ghost thread until the user clicks "+". Skip while a send is in flight — a
  // just-created thread is selected before the threads list refetch includes it.
  useEffect(() => {
    if (!threadsFetched || !chat.activeThreadId || chat.isThinking || chat.pending) return;
    if (!threads.some((th) => th.id === chat.activeThreadId)) chat.newThread();
  }, [threadsFetched, threads, chat]);

  // Grow the composer with its content (up to the CSS max-height), like a
  // desktop chat input. Runs on every input change; also collapses after send.
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [input]);

  const canSend = input.trim().length > 0 && !chat.isThinking;

  const submit = useCallback(() => {
    const text = input.trim();
    if (!text || chat.isThinking) return;
    setInput("");
    void chat.sendMessage(text);
  }, [input, chat]);

  const onComposerKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Panel: Enter sends; Shift+Enter inserts a newline. Ignore Enter while an
      // IME composition is in progress (isComposing; keyCode 229 covers older
      // Safari, which confirms a composition with isComposing already false) —
      // dictation and swipe input come through this path too.
      // Touch: Enter always inserts a newline (no Shift+Enter on a phone
      // keyboard); the send button is the only way to submit.
      if (
        !isTouch &&
        e.key === "Enter" &&
        !e.shiftKey &&
        !e.nativeEvent.isComposing &&
        e.nativeEvent.keyCode !== 229
      ) {
        e.preventDefault();
        submit();
      }
    },
    [submit, isTouch],
  );

  const startRename = useCallback(() => {
    if (!activeThread) return;
    setTitleDraft(activeThread.title);
    setIsEditingTitle(true);
  }, [activeThread]);

  const commitRename = useCallback(() => {
    setIsEditingTitle(false);
    const next = titleDraft.trim();
    if (activeThread && next && next !== activeThread.title) {
      renameThread.mutate({ threadId: activeThread.id, title: next });
    }
  }, [activeThread, titleDraft, renameThread]);

  const suggestions = [
    t("bookkeeper.suggestion1"),
    t("bookkeeper.suggestion2"),
    t("bookkeeper.suggestion3"),
  ];

  // Header icon buttons: 36px like the main app header's, 44px on touch
  // (docs/MOBILE.md §4.1 — `touchIcon` from the shared button variants).
  const headerButtonSize = isTouch ? ("touchIcon" as const) : ("icon" as const);
  const headerButtonClass = isTouch ? "active:bg-accent" : BOOKKEEPER_HEADER_BUTTON_CLASS;

  return (
    <>
      {/* Header — the main app header's chrome (bg-header tokens, h-12), so the
          docked panel's band lines up with UnifiedHeader beside it and follows
          the theme preset. `header.bg-header` also scopes the muted-text rule. */}
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 bg-header px-4 text-header-foreground">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <Sparkles className="h-4 w-4 shrink-0" />
          <div className="flex min-w-0 flex-1 items-baseline gap-2">
            <span className="shrink-0 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("bookkeeper.eyebrow")}
            </span>
            {isEditingTitle ? (
              <input
                autoFocus
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRename();
                  if (e.key === "Escape") setIsEditingTitle(false);
                }}
                aria-label={t("bookkeeper.rename")}
                className={cn(
                  "min-w-0 flex-1 rounded-md border border-input bg-background px-1.5 font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isTouch ? "text-base" : "text-sm",
                )}
              />
            ) : (
              <button
                type="button"
                onClick={activeThread ? startRename : undefined}
                title={activeThread ? t("bookkeeper.rename") : undefined}
                className={
                  isTouch
                    ? "min-w-0 truncate text-left text-base font-medium"
                    : "min-w-0 truncate text-left text-sm font-medium"
                }
              >
                {activeThread ? activeThread.title : t("bookkeeper.title")}
              </button>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-0.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size={headerButtonSize}
                className={headerButtonClass}
                title={t("bookkeeper.history")}
              >
                <HistoryIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>{t("bookkeeper.history")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {threads.length === 0 ? (
                <div className="px-2 py-3 text-xs text-muted-foreground">
                  {t("bookkeeper.noThreads")}
                </div>
              ) : (
                threads.map((th) => (
                  <DropdownMenuItem
                    key={th.id}
                    onSelect={() => chat.selectThread(th.id)}
                    className={
                      isTouch
                        ? "flex min-h-14 items-center justify-between gap-2"
                        : "flex items-center justify-between gap-2"
                    }
                  >
                    <span className="flex min-w-0 flex-col">
                      <span className={isTouch ? "truncate text-base" : "truncate text-sm"}>
                        {th.title}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {safeFormatDate(th.last_message_at, "d MMM yyyy")}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (chat.activeThreadId === th.id) chat.newThread();
                        deleteThread.mutate(th.id);
                      }}
                      className={
                        isTouch
                          ? "min-h-11 min-w-11 shrink-0 rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive active:bg-destructive/10"
                          : "shrink-0 rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      }
                      title={t("bookkeeper.delete")}
                    >
                      <Trash2 className={isTouch ? "mx-auto h-5 w-5" : "h-3.5 w-3.5"} />
                    </button>
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="ghost"
            size={headerButtonSize}
            className={headerButtonClass}
            title={t("bookkeeper.newChat")}
            onClick={() => chat.newThread()}
          >
            <Plus className="h-4 w-4" />
          </Button>
          {headerActions}
        </div>
      </header>

      {/* Messages */}
      <div
        ref={scrollRef}
        className={
          isTouch
            ? "min-h-0 flex-1 overflow-y-auto overscroll-contain"
            : "min-h-0 flex-1 overflow-y-auto"
        }
      >
        <div className="flex flex-col gap-4 px-4 pt-4">
          {showEmpty ? (
            <div className="flex flex-col items-center justify-center px-2 py-10 text-center text-muted-foreground">
              <Sparkles className="mb-4 h-12 w-12" />
              <div
                className={
                  isTouch
                    ? "mb-1 text-base font-medium text-foreground"
                    : "mb-1 text-sm font-medium text-foreground"
                }
              >
                {t("bookkeeper.emptyTitle")}
              </div>
              <p className={isTouch ? "mb-4 text-base" : "mb-4 max-w-xs text-xs"}>
                {t("bookkeeper.emptyBody")}
              </p>
              <div className={isTouch ? "flex w-full flex-col gap-2" : "flex w-full flex-col gap-1.5"}>
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => void chat.sendMessage(s)}
                    className={
                      isTouch
                        ? "min-h-14 rounded-lg border border-border px-4 py-3 text-left text-base hover:bg-muted active:bg-accent"
                        : "rounded-lg border border-border px-3 py-2 text-left text-xs hover:bg-muted"
                    }
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((m) => (
                <BookkeeperMessage
                  key={m.id}
                  role={m.role}
                  content={m.content}
                  chartSpec={m.chart_spec}
                  messageId={m.id}
                  coaProposal={m.coa_proposal}
                  touch={isTouch}
                />
              ))}
              {chat.pending && (
                <BookkeeperMessage role="user" content={chat.pending.userText} />
              )}
              {/* trim(): a model can stream a bare newline before its first
                  tool call — that must not paint an empty bubble. */}
              {chat.streamingText.trim() && (
                <BookkeeperMessage role="assistant" content={chat.streamingText} />
              )}
              {/* Shown on its own before the first delta, and BENEATH the
                  streamed bubble while a tool runs — the narration the model
                  wrote before calling it ("Let me pull the register…") stays
                  put rather than being swapped for the spinner. */}
              {chat.isThinking && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("bookkeeper.thinking")}
                </div>
              )}
            </>
          )}
          <div className="h-4" />
        </div>
      </div>

      {/* Composer */}
      <div
        className={
          isTouch
            ? "shrink-0 border-t border-border bg-background p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
            : "shrink-0 border-t border-border bg-background/95 p-3 backdrop-blur-sm"
        }
      >
        {isTouch ? (
          <div className="flex items-end gap-2">
            <Textarea
              ref={composerRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onComposerKeyDown}
              placeholder={t("bookkeeper.placeholder")}
              rows={1}
              // text-base (16px): below that, iOS Safari zooms the viewport on focus
              // and does not zoom back. Scoped to this composer rather than
              // raised in `ui/textarea.tsx`, so no other surface moves.
              className="max-h-40 min-h-[3rem] flex-1 resize-none overflow-y-auto text-base"
            />
            <Button
              type="button"
              size="touchIcon"
              // Touch has no hover, so without an explicit pressed state a tap
              // goes unacknowledged until the reply starts streaming. Scoped
              // here rather than added to the shared button variants.
              className="shrink-0 active:bg-primary/80"
              title={t("bookkeeper.send")}
              aria-label={t("bookkeeper.send")}
              disabled={!canSend}
              onClick={submit}
            >
              <Send className="h-5 w-5" />
            </Button>
          </div>
        ) : (
          <Textarea
            ref={composerRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onComposerKeyDown}
            placeholder={t("bookkeeper.placeholder")}
            rows={1}
            className="max-h-40 min-h-[2.5rem] resize-none overflow-y-auto"
          />
        )}
        <p
          className={
            isTouch
              ? "mt-1.5 text-xs text-muted-foreground"
              : "mt-1.5 text-[10px] text-muted-foreground"
          }
        >
          {t("bookkeeper.disclaimer")}
        </p>
      </div>
    </>
  );
}
